/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import { cssMap, jsx, cx } from '@atlaskit/css';
import { aiProactiveMessages } from '@atlaskit/editor-common/ai-messages';
import NodeIcon from '@atlaskit/icon/core/node';
import TextSpellcheckIcon from '@atlaskit/icon/core/text-spellcheck';
import { Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type {
	TransformAction,
	RecommendationDetails,
	RecommendationType,
} from '../../../pm-plugins/ai-proactive/api';
import { getRecommendationCard } from '../../../pm-plugins/ai-proactive/suggestion-setting-map';

const styles = cssMap({
	pressable: {
		color: token('color.text.subtlest'),
		fontWeight: token('font.weight.regular'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
		backgroundColor: token('color.background.neutral.subtle'),
	},

	pressableSelected: {
		textDecoration: 'underline',
	},

	pressableDisabled: {
		color: token('color.text.disabled'),
	},

	container: {
		// display: 'flex',
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		// flexDirection: 'column',
		gap: token('space.100'),
		// alignSelf: 'stretch',
		backgroundColor: token('elevation.surface'),
		cursor: 'pointer',
		borderRadius: token('border.radius.100'),
	},

	extraPadding: {
		paddingBlockStart: token('space.050'),
	},

	containerHovered: {
		backgroundColor: token('elevation.surface.hovered'),
	},

	containerSelected: {
		backgroundColor: token('color.background.selected'),
	},

	containerDisabled: {
		color: token('color.text.disabled'),
		cursor: 'not-allowed',
	},
});

type ListItemProps = {
	title: string;
	description?: string;
	onClick: () => void;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	iconBefore: React.ReactNode;
	iconAfter: React.ReactNode;
	actionButtons?: React.ReactNode[];
	isSelected?: boolean;
	isHovered?: boolean;
	isDisabled?: boolean;
};
const ListItem = (props: ListItemProps) => {
	const {
		title,
		description,
		onClick,
		onMouseEnter,
		onMouseLeave,
		isHovered,
		isSelected,
		isDisabled,
		iconBefore,
		iconAfter,
		actionButtons,
	} = props;

	return (
		// Disabled key events as the effect would be handled through ProseMirror selections
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, @atlassian/a11y/interactive-element-not-keyboard-focusable
		<div
			onClick={!isDisabled ? onClick : undefined}
			onMouseEnter={!isDisabled ? onMouseEnter : undefined}
			onMouseLeave={!isDisabled ? onMouseLeave : undefined}
			css={[
				styles.container,
				!isDisabled && isSelected && styles.containerSelected,
				!isDisabled && isHovered && !isSelected && styles.containerHovered,
				isDisabled && styles.containerDisabled,
			]}
		>
			<Inline space="space.150" alignBlock="center">
				{iconBefore}
				<Inline grow="fill">
					<Text
						weight="semibold"
						size="large"
						color={isDisabled ? 'color.text.disabled' : 'color.text'}
					>
						{title}
					</Text>
				</Inline>
				{iconAfter}
			</Inline>
			{!!description && (
				<Text color={isDisabled ? 'color.text.disabled' : 'color.text.subtle'}>{description}</Text>
			)}
			{!!actionButtons?.length && (
				<Inline space="space.150" alignBlock="center" xcss={styles.extraPadding}>
					{actionButtons}
				</Inline>
			)}
		</div>
	);
};

type RecommendationItemProps = {
	id: string;
	action?: TransformAction;
	type?: RecommendationType;
	details?: RecommendationDetails;
	isViewed: boolean;
	isHovered: boolean;
	isSelected: boolean;
	isDisabled?: boolean;
	onApply: () => void;
	onDismiss: () => void;
	onClick: () => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	onPreview: () => void;
};

const ItemButton = ({
	key,
	isHovered,
	isDisabled,
	canHover,
	onClick,
	buttonLabel,
}: {
	key: string;
	isHovered: boolean;
	isDisabled?: boolean;
	canHover?: boolean;
	onClick: () => void;
	buttonLabel: string;
}) => {
	return (
		<Pressable
			key={key}
			xcss={cx(
				styles.pressable,
				canHover && isHovered && styles.pressableSelected,
				isDisabled && styles.pressableDisabled,
			)}
			onClick={onClick}
			isDisabled={isDisabled}
		>
			{buttonLabel}
		</Pressable>
	);
};

export const RecommendationItem = (props: RecommendationItemProps) => {
	const {
		action,
		type,
		details,
		isViewed,
		isHovered,
		isSelected,
		isDisabled,
		onApply,
		onDismiss,
		onClick,
		onMouseEnter,
		onMouseLeave,
		onPreview,
	} = props;
	const { formatMessage } = useIntl();
	const {
		description: descriptionDescriptor,
		title: titleDescriptor,
		icon: Icon,
	} = getRecommendationCard({
		transformAction: action,
		type: type,
		details: details,
	});

	const title = formatMessage(titleDescriptor);
	const description = formatMessage(descriptionDescriptor);

	const previewButton = (
		<ItemButton
			key="preview"
			canHover
			isDisabled={isDisabled}
			isHovered={isHovered}
			onClick={onPreview}
			buttonLabel={formatMessage(aiProactiveMessages.recommendationPreviewButtonLabel)}
		/>
	);

	const insertButton = (
		<ItemButton
			key="replace"
			isDisabled={isDisabled}
			isHovered={isHovered}
			onClick={onApply}
			buttonLabel={formatMessage(aiProactiveMessages.recommendationReplaceButtonLabel)}
		/>
	);

	const dismissButton = (
		<ItemButton
			key="dismiss"
			isDisabled={isDisabled}
			isHovered={isHovered}
			onClick={onDismiss}
			buttonLabel={formatMessage(aiProactiveMessages.recommendationDismissButtonLabel)}
		/>
	);

	const actionButtons = isSelected ? [insertButton, dismissButton] : [previewButton, dismissButton];

	return (
		<ListItem
			title={title}
			description={description}
			isHovered={isHovered}
			isSelected={isSelected}
			isDisabled={isDisabled}
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			iconBefore={
				<Icon
					size="small"
					label={title}
					color={isDisabled ? token('color.icon.disabled') : token('color.icon')}
				/>
			}
			iconAfter={
				isViewed ? null : (
					<NodeIcon
						LEGACY_size="small"
						label="unread"
						LEGACY_primaryColor={
							isDisabled ? token('color.icon.disabled') : token('color.icon.information')
						}
					/>
				)
			}
			actionButtons={actionButtons}
		/>
	);
};

type SpellingGrammarItemProps = {
	errorCount: number;
	onClick: () => void;
};

export const SpellingGrammarItem = (props: SpellingGrammarItemProps) => {
	const { errorCount, onClick } = props;
	const { formatMessage } = useIntl();
	return (
		<ListItem
			title={formatMessage(aiProactiveMessages.spellingAndGrammarLabel)}
			onClick={onClick}
			iconBefore={
				<TextSpellcheckIcon
					label={formatMessage(aiProactiveMessages.spellingAndGrammarLabel)}
					color={token('color.icon')}
				/>
			}
			iconAfter={<Badge>{errorCount}</Badge>}
		/>
	);
};
